import styled from '@emotion/styled';
import { NavItem } from './NavItem';
import { NavElement } from './types';
import { Box, Divider, IconButton, Tooltip } from '@mui/material';
import { useAuth } from 'src/Common/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/HomeOutlined';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import LibraryAddCheckOutlinedIcon from '@mui/icons-material/LibraryAddCheckOutlined';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import CurrencyBitcoinOutlinedIcon from '@mui/icons-material/CurrencyBitcoinOutlined';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import SignalWifiStatusbarConnectedNoInternet4Icon from '@mui/icons-material/SignalWifiStatusbarConnectedNoInternet4';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import QueryStatsOutlinedIcon from '@mui/icons-material/QueryStatsOutlined';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import GavelIcon from '@mui/icons-material/Gavel';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { useAppMode, AppMode } from 'src/Common/context/AppModeContext';
import { StopAllButton } from 'src/Common/components/StopAllButton/StopAllButton';

const orderbookRoutes: NavElement[] = [
  {
    path: '/arbitrage',
    name: 'Home',
    icon: <HomeIcon />
  },
  {
    path: '/arbitrage/alerts',
    name: 'Alerts',
    icon: <NotificationsActiveOutlinedIcon />
  },
  {
    path: '/arbitrage/marked-as-read',
    name: 'Marked as read',
    icon: <LibraryAddCheckOutlinedIcon />
  },
  {
    path: '/arbitrage/cryptos-info',
    name: 'Cryptos info',
    icon: <CurrencyBitcoinOutlinedIcon />
  },
  {
    path: '/config-editor/crypto',
    name: 'Crypto Editor',
    icon: <SettingsSuggestIcon />
  },
  {
    path: '/arbitrage/exchanges-status',
    name: 'Exchanges Status',
    icon: <SignalWifiStatusbarConnectedNoInternet4Icon />
  },
  {
    path: '/arbitrage/services-status',
    name: 'Services Status',
    icon: <MonitorHeartOutlinedIcon />
  }
];

const predictionRoutes: NavElement[] = [
  {
    path: '/predictions/opportunities',
    name: 'Opportunities',
    icon: <TrendingUpIcon />
  },
  {
    path: '/predictions/positions',
    name: 'Positions',
    icon: <AccountBalanceWalletOutlinedIcon />
  },
  {
    path: '/predictions/unwinding',
    name: 'Unwinding',
    icon: <ExitToAppIcon />
  },
  {
    path: '/predictions/opening',
    name: 'Opening',
    icon: <RocketLaunchIcon />
  },
  {
    path: '/predictions/notifications',
    name: 'Notifications',
    icon: <NotificationsActiveOutlinedIcon />
  },
  {
    path: '/predictions/mapper',
    name: 'Prediction Mapper',
    icon: <AccountTreeOutlinedIcon />
  },
  {
    path: '/predictions/status',
    name: 'Predictions Status',
    icon: <QueryStatsOutlinedIcon />
  },
  {
    path: '/predictions/costs',
    name: 'Costs',
    icon: <ReceiptLongOutlinedIcon />
  }
];

export const SidebarNav = () => {
  const { logout } = useAuth();
  const { mode, setMode } = useAppMode();
  const navigate = useNavigate();

  const routes = mode === 'orderbooks' ? orderbookRoutes : predictionRoutes;

  const handleModeChange = (newMode: AppMode) => {
    if (newMode === mode) return;
    setMode(newMode);
    const firstRoute =
      newMode === 'orderbooks' ? orderbookRoutes[0] : predictionRoutes[0];
    navigate(firstRoute.path);
  };

  return (
    <Container>
      <div>
        <ModeToggle>
          <Tooltip title="Orderbooks" placement="right">
            <IconButton
              onClick={() => handleModeChange('orderbooks')}
              sx={{
                color: theme =>
                  mode === 'orderbooks'
                    ? theme.palette.primary.main
                    : theme.palette.grey[700],
                borderRadius: '5px',
                '& > svg': {
                  fontSize: '1.1rem'
                }
              }}
              size="small"
            >
              <ShowChartIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Predictions" placement="right">
            <IconButton
              onClick={() => handleModeChange('predictions')}
              sx={{
                color: theme =>
                  mode === 'predictions'
                    ? theme.palette.primary.main
                    : theme.palette.grey[700],
                borderRadius: '5px',
                '& > svg': {
                  fontSize: '1.1rem'
                }
              }}
              size="small"
            >
              <GavelIcon />
            </IconButton>
          </Tooltip>
        </ModeToggle>

        <Box sx={{ my: 1, mx: 0.5 }}>
          <Divider sx={{ borderColor: 'grey.800' }} />
        </Box>

        {routes.map(route => (
          <NavItem key={route.name} navItem={route} />
        ))}
      </div>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.5
        }}
      >
        {/* Master kill switch — prediction-side only. */}
        {mode === 'predictions' && <StopAllButton />}

        <Tooltip title="Logout" placement="right">
          <IconButton
            onClick={() => logout()}
            sx={{
              color: theme => theme.palette.grey[600],
              '& > svg': {
                fontSize: '1.1rem',
                '&:hover': {
                  color: theme => theme.palette.primary.main
                }
              }
            }}
            size="small"
          >
            <LogoutRoundedIcon />
          </IconButton>
        </Tooltip>
      </Box>
    </Container>
  );
};

const Container = styled.div(({ theme }) => ({
  width: '100%',
  paddingTop: '7rem',
  paddingBottom: '2rem',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'space-between',
  backgroundColor: theme.palette.background.default,
  filter: 'brightness(70%)'
}));

const ModeToggle = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '0.25rem'
});
